import { useState, useCallback } from 'react';

/**
 * Hook personalizado para consultar dados de empresas via CNPJ
 * Utiliza múltiplas APIs em ordem de prioridade:
 * 1. API local do backend (sem problemas de CORS)
 * 2. BrasilAPI (sem problemas de CORS)
 * 3. ReceitaWS direta (fallback, pode falhar por CORS)
 * @returns {Object} Objeto contendo função de busca, estado de loading e dados da empresa
 */
const useCnpj = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Busca os dados da empresa pelo CNPJ informado
   * @param {string} cnpj - CNPJ no formato com ou sem máscara
   * @returns {Promise<Object|null>} Dados da empresa ou null em caso de erro
   */
  const fetchCompanyData = useCallback(async (cnpj) => {
    console.log('🔍 [useCnpj] Buscando dados para CNPJ:', cnpj);
    
    if (!cnpj) {
      setError('CNPJ não informado');
      return null;
    }

    // Remove caracteres não numéricos
    const cleanCnpj = cnpj.replace(/\D/g, '');
    
    // Valida se tem 14 dígitos
    if (cleanCnpj.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return null;
    }

    // Validação básica do CNPJ
    if (!isValidCnpj(cleanCnpj)) {
      setError('CNPJ inválido');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Tentativa 1: API local do backend (sem problemas de CORS)
      let response;
      let data;
      
      try {
        console.log('🏠 [useCnpj] Tentando API local do backend...');
        const backendUrl = `/backend/banks/consultar-cnpj/${cleanCnpj}/`;
        
        response = await fetch(backendUrl);
        
        if (response.ok) {
          data = await response.json();
          console.log('✅ [useCnpj] Dados obtidos do backend:', data.nome || 'Dados encontrados');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro no servidor local');
        }
      } catch (backendError) {
        console.warn('🚨 [useCnpj] Backend falhou, tentando BrasilAPI...');
        
        // Tentativa 2: API BrasilAPI (gratuita e sem CORS)
        try {
          console.log('🇧🇷 [useCnpj] Tentativa 2: BrasilAPI');
          const brasilApiUrl = `https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`;
          console.log('📍 [useCnpj] URL BrasilAPI:', brasilApiUrl);
          
          response = await fetch(brasilApiUrl);
          console.log('📡 [useCnpj] BrasilAPI Response status:', response.status);
          
          if (response.ok) {
            const brasilApiData = await response.json();
            console.log('✅ [useCnpj] Dados recebidos da BrasilAPI:', brasilApiData);
            
            // Converte formato BrasilAPI para formato padrão
            data = {
              nome: brasilApiData.razao_social || '',
              fantasia: brasilApiData.nome_fantasia || '',
              logradouro: brasilApiData.logradouro || '',
              numero: brasilApiData.numero || '',
              complemento: brasilApiData.complemento || '',
              bairro: brasilApiData.bairro || '',
              municipio: brasilApiData.municipio || '',
              uf: brasilApiData.uf || '',
              cep: brasilApiData.cep ? brasilApiData.cep.replace('-', '') : '',
              telefone: brasilApiData.ddd_telefone_1 || '',
              email: brasilApiData.email || '',
              situacao: brasilApiData.descricao_situacao_cadastral || '',
              abertura: brasilApiData.data_inicio_atividade || '',
              atividade_principal: brasilApiData.cnae_fiscal_descricao ? [{text: brasilApiData.cnae_fiscal_descricao}] : [],
              natureza_juridica: brasilApiData.natureza_juridica || '',
              capital_social: brasilApiData.capital_social ? String(brasilApiData.capital_social) : ''
            };
            console.log('🔄 [useCnpj] Dados convertidos:', data);
          } else {
            throw new Error('BrasilAPI não disponível');
          }
        } catch (brasilApiError) {
          console.warn('🚨 [useCnpj] BrasilAPI falhou:', brasilApiError.message);
          
          // Tentativa 3: API ReceitaWS direta (pode falhar por CORS)
          console.log('📃 [useCnpj] Tentativa 3: ReceitaWS direta');
          const receitaWsUrl = `https://www.receitaws.com.br/v1/cnpj/${cleanCnpj}`;
          console.log('📍 [useCnpj] URL ReceitaWS:', receitaWsUrl);
          
          response = await fetch(receitaWsUrl);
          console.log('📡 [useCnpj] ReceitaWS Response status:', response.status);
          
          if (!response.ok) {
            throw new Error('Todas as APIs falharam - verifique sua conexão ou tente novamente');
          }
          
          data = await response.json();
          console.log('✅ [useCnpj] Dados recebidos da ReceitaWS:', data);
        }
      }

      // Verifica se houve erro na consulta
      if (data.status === 'ERROR') {
        console.log('❌ [useCnpj] API retornou erro:', data.message);
        throw new Error(data.message || 'CNPJ não encontrado');
      }

      console.log('🎯 [useCnpj] Processando dados para preenchimento...');
      setLoading(false);
      
      // Retorna os dados formatados para os campos do formulário
      const formattedData = {
        name: data.nome || data.fantasia || '',
        fantasyName: data.fantasia || '',
        street: data.logradouro || '',
        number: data.numero || '',
        complement: data.complemento || '',
        neighborhood: data.bairro || '',
        city: data.municipio || '',
        state: data.uf || '',
        zipcode: data.cep ? data.cep.replace(/\D/g, '') : '',
        phone: data.telefone || '',
        email: data.email || '',
        situation: data.situacao || '',
        openingDate: data.abertura || '',
        mainActivity: data.atividade_principal ? data.atividade_principal[0]?.text : '',
        legalNature: data.natureza_juridica || '',
        shareCapital: data.capital_social || ''
      };
      
      console.log('📋 [useCnpj] Dados formatados para o formulário:', formattedData);
      return formattedData;

    } catch (err) {
      console.error('💥 [useCnpj] Erro final:', err);
      setLoading(false);
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Validação básica do CNPJ
   * @param {string} cnpj - CNPJ apenas com números
   * @returns {boolean} True se o CNPJ é válido
   */
  const isValidCnpj = (cnpj) => {
    if (!cnpj || cnpj.length !== 14) return false;

    // Elimina CNPJs conhecidos como inválidos
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    // Algoritmo de validação do CNPJ
    let length = cnpj.length - 2;
    let numbers = cnpj.substring(0, length);
    let digits = cnpj.substring(length);
    let sum = 0;
    let pos = length - 7;

    for (let i = length; i >= 1; i--) {
      sum += numbers.charAt(length - i) * pos--;
      if (pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;

    length = length + 1;
    numbers = cnpj.substring(0, length);
    sum = 0;
    pos = length - 7;

    for (let i = length; i >= 1; i--) {
      sum += numbers.charAt(length - i) * pos--;
      if (pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(digits.charAt(1));
  };

  return {
    fetchCompanyData,
    loading,
    error,
    clearError: () => setError(null)
  };
};

export default useCnpj;
